import React from "react";
import { FieldArray, useFormikContext } from "formik";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  IconButton,
  Button,
  Divider,
  Stack,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { newChildStepTemplate } from "../helper/WorkflowConfigTemplates";
import ChildStepRejectForm from "./ChildStepRejectForm";
import ChildStepApproveForm from "./ChildStepApproveForm";

export default function ChildStepsEditor({ roles = [], stepIndex }) {
  const { values, handleChange, setFieldValue } = useFormikContext();

  const step = values?.steps?.[stepIndex];
  if (!step) return null;

  const childPath = `steps[${stepIndex}].childSteps`;
  const childSteps = step.childSteps || [];

  const getNewChild = () => ({
    childStepName: "",
    sequence: 1,
    roleId: "",
    tatInHours: "",
    isActive: true,
    description: "",
    isOptional: false,
    requireCommentOnApprove: true,
    requireCommentOnReject: true,
    requireAttachmentOnApprove: false,
    requireAttachmentOnReject: false,
    allowParallelExecution: false,
  });

  return (
    <FieldArray name={childPath}>
      {(arrayHelpers) => (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              Child Steps
            </Typography>

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                arrayHelpers.push(newChildStepTemplate(step.workflowConfigurationStepId ?? null));
              }}
            >
              Add Child Step
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {childSteps.length === 0 ? (
            <Typography sx={{ fontSize: 13, opacity: 0.75 }}>
              No child steps. Add child steps if multiple departments must “Attend/Review” within this step.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {childSteps.map((c, cIdx) => {
                const base = `${childPath}[${cIdx}]`;
                const nestedPath = `${base}.nestedChildSteps`;
                const nested = c.nestedChildSteps || [];

                return (
                  <Paper key={c.workflowChildStepId || cIdx} sx={{ p: 2, position: "relative" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        Child Step {cIdx + 1}
                      </Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => arrayHelpers.remove(cIdx)}
                          title="Delete child step"
                          variant="outlined"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        {/* <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const nextSequence = (nested?.length || 0) + 1;

                            const updated = [
                              ...nested,
                              {
                                ...getNewChild(),
                                sequence: nextSequence,
                              },
                            ];

                            setFieldValue(nestedPath, updated);
                          }}
                        >
                          Add Child
                        </Button> */}
                      </Stack>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <TextField
                          fullWidth
                          label="Child Step Name"
                          name={`${base}.childStepName`}
                          value={c.childStepName ?? ""}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          select
                          fullWidth
                          label="Role"
                          name={`${base}.roleId`}
                          value={c.roleId ?? ""}
                          onChange={handleChange}
                        >
                          {roles.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label="TAT (Hours)"
                          name={`${base}.tatInHours`}
                          value={c.tatInHours ?? ""}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!c.isActive}
                              name={`${base}.isActive`}
                              onChange={handleChange}
                            />
                          }
                          label="Active"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!c.allowParallelExecution}
                              name={`${base}.allowParallelExecution`}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue(`${base}.allowParallelExecution`, checked);

                                // If disabling parallel, clear minimumApprovalCount
                                if (!checked) {
                                  setFieldValue(`${base}.minimumApprovalCount`, null);
                                } else {
                                  // if enabling parallel and min count is empty, set default 1
                                  if (!c.minimumApprovalCount) {
                                    setFieldValue(`${base}.minimumApprovalCount`, 1);
                                  }
                                }
                              }}
                            />
                          }
                          label="Allow Parallel Execution"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <ChildStepRejectForm stepIndex={stepIndex} childStepIndex={cIdx} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <ChildStepApproveForm stepIndex={stepIndex} childStepIndex={cIdx} />
                      </Grid>
                      {/* Nested Child Steps */}
                      {/* {nested.length > 0 && (
                        <Box
                          sx={{
                            mt: 2,
                            pl: 3,
                            borderLeft: "2px dashed #ccc",
                            width: '100%'
                          }}
                        >
                          <Typography fontSize={13} fontWeight={600} mb={1}>
                            Nested Child Steps
                          </Typography>

                          <Stack spacing={2}>
                            {nested.map((n, nIdx) => {
                              const nestedBase = `${nestedPath}[${nIdx}]`;

                              return (
                                <Paper key={nIdx} sx={{ p: 2 }}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Typography fontSize={12} fontWeight={600}>
                                      Nested Step {nIdx + 1}
                                    </Typography>

                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const updated = [...nested];
                                        updated.splice(nIdx, 1);
                                        setFieldValue(nestedPath, updated);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>

                                  <Grid container spacing={2}>
                                    <Grid size={{ md: 8, xs: 12 }}>
                                      <TextField
                                        fullWidth
                                        label="Step Name"
                                        name={`${nestedBase}.childStepName`}
                                        value={n.childStepName || ""}
                                        onChange={handleChange}
                                        size="small"
                                      />
                                    </Grid>

                                    <Grid size={{ md: 4, xs: 12 }}>
                                      <TextField
                                        select
                                        fullWidth
                                        label="Role"
                                        name={`${nestedBase}.roleId`}
                                        value={n.roleId || ""}
                                        onChange={handleChange}
                                        size="small"
                                      >
                                        {roles.map((r) => (
                                          <MenuItem
                                            key={r.value}
                                            value={r.value}
                                          >
                                            {r.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>

                                    <Grid size={{ md: 4, xs: 12 }}>
                                      <TextField
                                        type="number"
                                        fullWidth
                                        label="TAT (Hours)"
                                        name={`${nestedBase}.tatInHours`}
                                        value={n.tatInHours || ""}
                                        onChange={handleChange}
                                        size="small"
                                      />
                                    </Grid>

                                    <Grid size={{ md: 2, xs: 12 }}>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={!!n.isActive}
                                            name={`${nestedBase}.isActive`}
                                            onChange={handleChange}
                                          />
                                        }
                                        label="Active"
                                      />
                                    </Grid>
                                    <Grid size={{ md: 4, xs: 12 }}>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={!!n.allowParallelExecution}
                                            name={`${nestedBase}.allowParallelExecution`}
                                            onChange={(e) => {
                                              const checked = e.target.checked;
                                              setFieldValue(`${nestedBase}.allowParallelExecution`, checked);

                                              // If disabling parallel, clear minimumApprovalCount
                                              if (!checked) {
                                                setFieldValue(`${nestedBase}.minimumApprovalCount`, null);
                                              } else {
                                                // if enabling parallel and min count is empty, set default 1
                                                if (!n.minimumApprovalCount) {
                                                  setFieldValue(`${nestedBase}.minimumApprovalCount`, 1);
                                                }
                                              }
                                            }}
                                          />
                                        }
                                        label="Allow Parallel Execution"
                                      />
                                    </Grid>
                                  </Grid>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </Box>
                      )} */}
                      {/* <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Minimum Approval Count"
                          name={`${base}.minimumApprovalCount`}
                          value={c.allowParallelExecution ? (c.minimumApprovalCount ?? "") : ""}
                          onChange={handleChange}
                          disabled={!c.allowParallelExecution}
                        />
                      </Grid> */}
                    </Grid>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>
      )}
    </FieldArray>
  );
}
